import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { articleFournisseurService, fournisseurService, ArticleFournisseur } from '../../api/services';
import { Fournisseur } from '../../types';

/** Catalogue article-fournisseur : conditions d'achat par fournisseur (réf., prix, délai,
 *  conditionnement, fournisseur préféré). Affiché sur la fiche article en édition. */
const ArticleSuppliers: React.FC<{ articleId: number }> = ({ articleId }) => {
  const [rows, setRows] = useState<ArticleFournisseur[]>([]);
  const [fournisseurs, setFournisseurs] = useState<Fournisseur[]>([]);
  const [busy, setBusy] = useState(true);

  const [fournisseurId, setFournisseurId] = useState<number | ''>('');
  const [referenceFournisseur, setRef] = useState('');
  const [prixAchat, setPrix] = useState('');
  const [delaiJours, setDelai] = useState('');
  const [conditionnement, setCond] = useState('');
  const [prefere, setPrefere] = useState(false);

  const load = () => {
    setBusy(true);
    Promise.all([articleFournisseurService.list(articleId), fournisseurService.getAll({ size: 1000 })])
      .then(([r, f]) => { setRows(r); setFournisseurs(f.content); })
      .catch(() => toast.error('Erreur de chargement'))
      .finally(() => setBusy(false));
  };

  useEffect(load, [articleId]);

  const add = async () => {
    if (fournisseurId === '') { toast.error('Choisissez un fournisseur'); return; }
    try {
      await articleFournisseurService.create(articleId, {
        fournisseurId: Number(fournisseurId),
        referenceFournisseur: referenceFournisseur || undefined,
        prixAchat: prixAchat ? Number(prixAchat) : undefined,
        delaiJours: delaiJours ? Number(delaiJours) : undefined,
        conditionnement: conditionnement ? Number(conditionnement) : undefined,
        prefere,
      });
      toast.success('Fournisseur ajouté');
      setFournisseurId(''); setRef(''); setPrix(''); setDelai(''); setCond(''); setPrefere(false);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Ajout impossible (déjà lié ?)');
    }
  };

  const togglePrefere = async (row: ArticleFournisseur) => {
    try {
      await articleFournisseurService.update(articleId, row.id, { ...row, prefere: !row.prefere });
      load();
    } catch { toast.error('Erreur'); }
  };

  const remove = async (id: number) => {
    try { await articleFournisseurService.delete(articleId, id); load(); }
    catch { toast.error('Suppression impossible'); }
  };

  return (
    <div className="card space-y-4">
      <h2 className="text-sm font-semibold text-gray-900">Fournisseurs de cet article</h2>

      {busy ? (
        <p className="text-sm text-gray-500">Chargement…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">Aucun fournisseur lié.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Fournisseur</th>
                <th className="px-3 py-2 text-left">Réf.</th>
                <th className="px-3 py-2 text-right">Prix achat</th>
                <th className="px-3 py-2 text-right">Délai (j)</th>
                <th className="px-3 py-2 text-center">Préféré</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-2 font-medium">{r.fournisseurRaisonSociale}</td>
                  <td className="px-3 py-2 text-gray-600">{r.referenceFournisseur || '—'}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.prixAchat != null ? Number(r.prixAchat).toFixed(3) : '—'}</td>
                  <td className="px-3 py-2 text-right">{r.delaiJours ?? '—'}</td>
                  <td className="px-3 py-2 text-center">
                    <button type="button" onClick={() => togglePrefere(r)}
                      className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                        r.prefere ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-500'}`}>
                      {r.prefere ? '★ Préféré' : 'Définir'}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button type="button" onClick={() => remove(r.id)} className="text-red-600 text-xs hover:underline">
                      Retirer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end border-t border-gray-100 pt-4">
        <div className="md:col-span-2">
          <label className="label">Fournisseur</label>
          <select value={fournisseurId} onChange={(e) => setFournisseurId(e.target.value ? Number(e.target.value) : '')}
            className="input-field">
            <option value="">—</option>
            {fournisseurs.map((f) => <option key={f.id} value={f.id}>{f.raisonSociale}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Réf.</label>
          <input value={referenceFournisseur} onChange={(e) => setRef(e.target.value)} className="input-field" />
        </div>
        <div>
          <label className="label">Prix</label>
          <input type="number" step="0.001" value={prixAchat} onChange={(e) => setPrix(e.target.value)} className="input-field" />
        </div>
        <div>
          <label className="label">Délai (j)</label>
          <input type="number" value={delaiJours} onChange={(e) => setDelai(e.target.value)} className="input-field" />
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-sm">
            <input type="checkbox" checked={prefere} onChange={(e) => setPrefere(e.target.checked)} /> Préféré
          </label>
        </div>
        <div className="md:col-span-6">
          <button type="button" onClick={add} className="btn-primary">Ajouter le fournisseur</button>
        </div>
      </div>
    </div>
  );
};

export default ArticleSuppliers;
